import { toJS } from "mobx";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";

export type LogoTransform = {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
};

const DEFAULT_LOGO = { x: 0, y: 0, width: 80, height: 80, rotation: 0 };
const DISPLAY_WIDTH = 600;
const DISPLAY_HEIGHT = 450;

export interface UseLogoPlacementOptions {
  selectedPsdId: number | null;
  zoom?: number;
  psdTemplate?: {
    canvas_w?: number | null;
    canvas_h?: number | null;
    default_x?: number;
    default_y?: number;
    default_width?: number;
    default_height?: number;
    print_area_x?: number;
    print_area_y?: number;
    print_area_width?: number;
    print_area_height?: number;
  } | null;
  onError?: (title: string, msg: string) => void;
  onLogoLoaded?: (info: { width: number; height: number; mimeType: string }) => void;
}

export type PrintAreaBounds = {
  left: number;
  top: number;
  width: number;
  height: number;
} | null;

/** Scale and offset when image uses object-fit: contain within the display box. */
export type ImageDisplayTransform = {
  scale: number;
  offsetX: number;
  offsetY: number;
};

function getRotatedBounds(width: number, height: number, rotationDeg: number) {
  const theta = (Math.abs(rotationDeg) * Math.PI) / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  return {
    width: Math.abs(width * cos) + Math.abs(height * sin),
    height: Math.abs(width * sin) + Math.abs(height * cos),
  };
}

function clampTransformToBounds(
  transform: LogoTransform,
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
): LogoTransform {
  let next = { ...transform };
  const boundsWidth = Math.max(1, bounds.maxX - bounds.minX);
  const boundsHeight = Math.max(1, bounds.maxY - bounds.minY);

  const currentRotated = getRotatedBounds(next.width, next.height, next.rotation);
  if (currentRotated.width > boundsWidth || currentRotated.height > boundsHeight) {
    const scale = Math.min(
      boundsWidth / currentRotated.width,
      boundsHeight / currentRotated.height,
    );
    next = {
      ...next,
      width: Math.max(20, next.width * scale),
      height: Math.max(20, next.height * scale),
    };
  }

  const rotated = getRotatedBounds(next.width, next.height, next.rotation);
  const cx = next.x + next.width / 2;
  const cy = next.y + next.height / 2;

  const minCx = bounds.minX + rotated.width / 2;
  const maxCx = bounds.maxX - rotated.width / 2;
  const minCy = bounds.minY + rotated.height / 2;
  const maxCy = bounds.maxY - rotated.height / 2;

  const clampedCx =
    minCx <= maxCx
      ? Math.max(minCx, Math.min(cx, maxCx))
      : (bounds.minX + bounds.maxX) / 2;
  const clampedCy =
    minCy <= maxCy
      ? Math.max(minCy, Math.min(cy, maxCy))
      : (bounds.minY + bounds.maxY) / 2;

  return {
    ...next,
    x: clampedCx - next.width / 2,
    y: clampedCy - next.height / 2,
  };
}

function getBoundsFromPrintArea(
  printAreaBounds: PrintAreaBounds,
): { minX: number; minY: number; maxX: number; maxY: number } {
  if (printAreaBounds) {
    return {
      minX: printAreaBounds.left,
      minY: printAreaBounds.top,
      maxX: printAreaBounds.left + printAreaBounds.width,
      maxY: printAreaBounds.top + printAreaBounds.height,
    };
  }
  return { minX: 0, minY: 0, maxX: DISPLAY_WIDTH, maxY: DISPLAY_HEIGHT };
}

export function useLogoPlacement({
  selectedPsdId,
  zoom = 1,
  psdTemplate,
  onError,
  onLogoLoaded,
}: UseLogoPlacementOptions) {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoOriginalSize, setLogoOriginalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [logoTransform, setLogoTransform] =
    useState<LogoTransform>(DEFAULT_LOGO);
  const [interactionMode, setInteractionMode] = useState<
    "idle" | "dragging" | "resizing"
  >("idle");

  const previewRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const logoAspectRef = useRef<number>(1);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });

  // Display transform: maps canvas coords to display (object-fit: contain = uniform scale + center)
  const displayTransform = useMemo(() => {
    if (!psdTemplate) return null;
    const canvasW = psdTemplate.canvas_w;
    const canvasH = psdTemplate.canvas_h;
    if (!canvasW || !canvasH || canvasW <= 0 || canvasH <= 0) {
      // Fallback: assume display = canvas (no letterboxing)
      return {
        scale: 1,
        offsetX: 0,
        offsetY: 0,
        canvasW: DISPLAY_WIDTH,
        canvasH: DISPLAY_HEIGHT,
      };
    }
    const scale = Math.min(DISPLAY_WIDTH / canvasW, DISPLAY_HEIGHT / canvasH);
    const offsetX = (DISPLAY_WIDTH - canvasW * scale) / 2;
    const offsetY = (DISPLAY_HEIGHT - canvasH * scale) / 2;
    return { scale, offsetX, offsetY, canvasW, canvasH };
  }, [psdTemplate]);

  // Compute print area bounds in display coordinates (for clipping & constraints)
  const printAreaBounds = useMemo((): PrintAreaBounds => {
    if (!psdTemplate || !displayTransform) return null;
    const paX = psdTemplate.print_area_x;
    const paY = psdTemplate.print_area_y;
    const paW = psdTemplate.print_area_width;
    const paH = psdTemplate.print_area_height;
    if (
      paX == null ||
      paY == null ||
      paW == null ||
      paH == null ||
      paW <= 0 ||
      paH <= 0
    ) {
      return null;
    }
    const { scale, offsetX, offsetY } = displayTransform;
    return {
      left: offsetX + paX * scale,
      top: offsetY + paY * scale,
      width: paW * scale,
      height: paH * scale,
    };
  }, [psdTemplate, displayTransform]);

  // Reset logo position when PSD template changes
  useEffect(() => {
    if (!selectedPsdId || !psdTemplate || !displayTransform) return;
    console.log("psdTemplate", toJS(psdTemplate));
    const { scale, offsetX, offsetY } = displayTransform;

    let x = offsetX + (psdTemplate.default_x ?? 50) * scale;
    let y = offsetY + (psdTemplate.default_y ?? 50) * scale;
    let w = (psdTemplate.default_width || DEFAULT_LOGO.width) * scale;
    let h = (psdTemplate.default_height || DEFAULT_LOGO.height) * scale;

    // Use print area bounds if available; otherwise full preview
    const minX = printAreaBounds ? printAreaBounds.left : offsetX;
    const minY = printAreaBounds ? printAreaBounds.top : offsetY;
    const maxX = printAreaBounds
      ? printAreaBounds.left + printAreaBounds.width
      : DISPLAY_WIDTH;
    const maxY = printAreaBounds
      ? printAreaBounds.top + printAreaBounds.height
      : DISPLAY_HEIGHT;

    if (logoUrl) {
      const boundsW = Math.max(1, maxX - minX);
      const boundsH = Math.max(1, maxY - minY);
      const logoAspect = Math.max(0.05, logoAspectRef.current || 1);
      const targetW = boundsW * 0.9;
      const targetH = boundsH * 0.9;
      const fitHeight = Math.min(targetH, targetW / logoAspect);
      h = Math.max(20, fitHeight);
      w = Math.max(20, fitHeight * logoAspect);
      x = minX + (boundsW - w) / 2;
      y = minY + (boundsH - h) / 2;
    } else {
      const maxW = DISPLAY_WIDTH * 0.4;
      const maxH = DISPLAY_HEIGHT * 0.4;
      w = Math.min(Math.max(w, 40), maxW);
      h = Math.min(Math.max(h, 40), maxH);
    }

    setLogoTransform(
      clampTransformToBounds(
        { x, y, width: w, height: h, rotation: 0 },
        { minX, minY, maxX, maxY },
      ),
    );
  }, [selectedPsdId, psdTemplate, printAreaBounds, displayTransform, logoUrl]);

  const handleLogoUrl = useCallback(
    (url: string | null) => {
      const normalized = String(url ?? "").trim();
      if (!normalized) {
        setLogoUrl(null);
        setLogoOriginalSize(null);
        return;
      }
      setLogoUrl(normalized);
      setLogoOriginalSize(null);
      const img = new Image();
      img.onload = () => {
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        logoAspectRef.current = width > 0 && height > 0 ? width / height : 1;
        setLogoOriginalSize({ width, height });
        onLogoLoaded?.({ width, height, mimeType: "image/*" });
      };
      img.onerror = () => {
        onError?.("Invalid logo", "Could not load selected logo.");
      };
      img.src = normalized;
    },
    [onError, onLogoLoaded],
  );

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (!logoUrl || interactionMode === "resizing") return;
      e.preventDefault();
      setInteractionMode("dragging");
      const rect = previewRef.current?.getBoundingClientRect();
      if (rect) {
        const t = logoTransformRef.current;
        const safeZoom = Math.max(0.25, zoom || 1);
        // Use logical transform origin (not rotated DOM bbox) for stable dragging.
        dragOffsetRef.current = {
          x: (e.clientX - rect.left) / safeZoom - t.x,
          y: (e.clientY - rect.top) / safeZoom - t.y,
        };
      }
    },
    [logoUrl, interactionMode, zoom],
  );

  const logoTransformRef = useRef(logoTransform);
  logoTransformRef.current = logoTransform;

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setInteractionMode("resizing");
    const t = logoTransformRef.current;
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: t.width,
      height: t.height,
    };
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const rect = previewRef.current?.getBoundingClientRect();
      if (!rect || interactionMode === "idle") return;

      const pa = printAreaBounds;
      const minX = pa ? pa.left : 0;
      const minY = pa ? pa.top : 0;
      const maxX = pa ? pa.left + pa.width : DISPLAY_WIDTH;
      const maxY = pa ? pa.top + pa.height : DISPLAY_HEIGHT;
      const bounds = { minX, minY, maxX, maxY };
      const safeZoom = Math.max(0.25, zoom || 1);

      setLogoTransform((prev) => {
        if (interactionMode === "dragging") {
          const { x: ox, y: oy } = dragOffsetRef.current;
          const rawX = (e.clientX - rect.left) / safeZoom - ox;
          const rawY = (e.clientY - rect.top) / safeZoom - oy;
          return clampTransformToBounds({ ...prev, x: rawX, y: rawY }, bounds);
        }
        if (interactionMode === "resizing") {
          const {
            x: sx,
            y: sy,
            width: sw,
          } = resizeStartRef.current;
          const deltaX = (e.clientX - sx) / safeZoom;
          const deltaY = (e.clientY - sy) / safeZoom;
          const aspect = Math.max(
            0.05,
            logoAspectRef.current || prev.width / prev.height || 1,
          );
          const theta = (prev.rotation * Math.PI) / 180;
          const ux = Math.cos(theta);
          const uy = Math.sin(theta);
          const vx = -Math.sin(theta);
          const vy = Math.cos(theta);
          // Growth direction for bottom-right handle in screen space.
          const growthX = ux + (1 / aspect) * vx;
          const growthY = uy + (1 / aspect) * vy;
          const growthLen = Math.hypot(growthX, growthY) || 1;
          const dirX = growthX / growthLen;
          const dirY = growthY / growthLen;
          const signedDelta = deltaX * dirX + deltaY * dirY;
          const nextWidth = Math.max(20, sw + signedDelta);
          const nextHeight = Math.max(20, nextWidth / aspect);
          return clampTransformToBounds(
            { ...prev, width: nextWidth, height: nextHeight },
            bounds,
          );
        }
        return prev;
      });
    },
    [interactionMode, printAreaBounds, zoom],
  );

  const handleMouseUp = useCallback(() => {
    setInteractionMode("idle");
  }, []);

  useEffect(() => {
    if (interactionMode === "idle") return;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [interactionMode, handleMouseMove, handleMouseUp]);

  const rotate = useCallback((delta: number) => {
    setLogoTransform((p) => {
      const bounds = getBoundsFromPrintArea(printAreaBounds);
      return clampTransformToBounds(
        { ...p, rotation: p.rotation + delta },
        bounds,
      );
    });
  }, [printAreaBounds]);

  const setRotation = useCallback((value: number) => {
    setLogoTransform((p) => {
      const bounds = getBoundsFromPrintArea(printAreaBounds);
      return clampTransformToBounds({ ...p, rotation: value }, bounds);
    });
  }, [printAreaBounds]);

  const getCanvasCoords = useCallback(
    (canvasW: number, canvasH: number) => {
      if (!displayTransform) return null;
      const { scale, offsetX, offsetY } = displayTransform;
      // Inverse: display coords → canvas coords
      return {
        x: Math.round((logoTransform.x - offsetX) / scale),
        y: Math.round((logoTransform.y - offsetY) / scale),
        width: Math.round(logoTransform.width / scale),
        height: Math.round(logoTransform.height / scale),
        rotation: logoTransform.rotation,
      };
    },
    [logoTransform, displayTransform],
  );

  const getSelectedLogoUrl = useCallback((): string | null => logoUrl, [logoUrl]);

  return {
    logoUrl,
    logoOriginalSize,
    logoTransform,
    isDragging: interactionMode === "dragging",
    printAreaBounds,
    previewRef,
    logoRef,
    handleLogoUrl,
    handleDrop,
    handleDragOver,
    handleDragStart,
    handleResizeStart,
    rotate,
    setRotation,
    getCanvasCoords,
    getSelectedLogoUrl,
    displaySize: { width: DISPLAY_WIDTH, height: DISPLAY_HEIGHT },
  };
}
